import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import { Search, ShieldOff, ShieldCheck } from "lucide-react";
import { DashboardShell } from "@/components/DashboardShell";
import { SUPER_ADMIN_NAV } from "@/lib/nav";
import { fetchPartners, setAccountStatus } from "@/lib/super-admin";

export const Route = createFileRoute("/_authenticated/super-admin/partners")({
  head: () => ({
    meta: [
      { title: "Partners · Landoo Super Admin" },
      { name: "description", content: "View all partners and suspend or restore accounts." },
    ],
  }),
  component: PartnersPage,
});

function PartnersPage() {
  const qc = useQueryClient();
  const { data = [], isLoading } = useQuery({
    queryKey: ["super-admin", "partners"],
    queryFn: fetchPartners,
  });
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState<"all" | "active" | "suspended">("all");

  const rows = useMemo(() => {
    const t = q.trim().toLowerCase();
    return data.filter((p) => {
      const status = p.account_status ?? "active";
      if (statusFilter !== "all" && status !== statusFilter) return false;
      if (!t) return true;
      return (
        (p.full_name ?? "").toLowerCase().includes(t) ||
        (p.email ?? "").toLowerCase().includes(t) ||
        (p.mobile ?? "").includes(t) ||
        (p.city ?? "").toLowerCase().includes(t)
      );
    });
  }, [data, q, statusFilter]);

  const toggle = async (id: string, next: "active" | "suspended") => {
    try {
      await setAccountStatus(id, next);
      toast.success(next === "suspended" ? "Partner suspended" : "Partner restored");
      qc.invalidateQueries({ queryKey: ["super-admin", "partners"] });
      qc.invalidateQueries({ queryKey: ["super-admin", "stats"] });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Could not update partner");
    }
  };

  return (
    <DashboardShell title="Partners" role="Super Admin" items={SUPER_ADMIN_NAV}>
      <div className="mb-3 grid grid-cols-[minmax(0,1fr)_auto] items-center gap-2 sm:flex sm:flex-wrap sm:justify-between">
        <div className="relative min-w-0">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search partners…"
            className="w-full min-w-0 rounded-full border border-border bg-card py-2 pl-9 pr-3 text-xs outline-none focus:border-brand sm:w-72"
          />
        </div>
        <div className="flex shrink-0 flex-wrap gap-1.5">
          {(["all", "active", "suspended"] as const).map((s) => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              className={`rounded-full px-3 py-1.5 text-xs font-semibold capitalize ${
                statusFilter === s
                  ? "bg-brown-dark text-primary-foreground"
                  : "bg-secondary text-brown-dark"
              }`}
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      {isLoading ? (
        <div className="rounded-2xl border border-border bg-card p-6 text-sm text-muted-foreground">
          Loading partners…
        </div>
      ) : rows.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border bg-card p-6 text-sm text-muted-foreground">
          No partners found.
        </div>
      ) : (
        <div className="space-y-2">
          {rows.map((p) => {
            const status = p.account_status ?? "active";
            const suspended = status === "suspended";
            return (
              <div key={p.id} className="rounded-2xl border border-border bg-card p-4 shadow-soft">
                <div className="grid grid-cols-[minmax(0,1fr)_auto] items-start gap-3 sm:flex sm:flex-wrap sm:justify-between">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-1.5">
                      <span
                        className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${
                          suspended ? "bg-coral/15 text-coral" : "bg-rent/15 text-rent"
                        }`}
                      >
                        {suspended ? "Suspended" : "Active"}
                      </span>
                      {p.partner_type && (
                        <span className="rounded-full bg-secondary px-2 py-0.5 text-[10px] font-semibold text-brown-dark">
                          {p.partner_type}
                        </span>
                      )}
                    </div>
                    <h3 className="mt-1 truncate text-sm font-semibold text-brown-dark">
                      {p.full_name ?? "Unnamed partner"}
                    </h3>
                    <p className="truncate text-xs text-muted-foreground">
                      {p.email ?? "—"} · {p.mobile ?? "—"} · {p.city ?? "—"}
                    </p>
                  </div>
                  <div className="flex flex-wrap justify-end gap-1.5">
                    {suspended ? (
                      <button
                        onClick={() => toggle(p.id, "active")}
                        className="inline-flex items-center gap-1 rounded-full bg-rent px-3 py-1.5 text-xs font-semibold text-white"
                      >
                        <ShieldCheck className="h-3.5 w-3.5" /> Restore
                      </button>
                    ) : (
                      <button
                        onClick={() => {
                          if (
                            !confirm(
                              `Suspend ${p.full_name ?? "this partner"}? Their listings will stop appearing.`,
                            )
                          )
                            return;
                          toggle(p.id, "suspended");
                        }}
                        className="inline-flex items-center gap-1 rounded-full bg-coral px-3 py-1.5 text-xs font-semibold text-white"
                      >
                        <ShieldOff className="h-3.5 w-3.5" /> Suspend
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </DashboardShell>
  );
}
